import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Lightbulb, Zap, Rocket, Brain, Target, Code } from 'lucide-react';

const Innovation = () => {
  const innovationPrinciples = [
    {
      icon: <Brain className="h-8 w-8" />,
      title: "Creative Thinking",
      description: "We encourage our team to think outside the box and challenge conventional approaches to problem-solving."
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Rapid Prototyping",
      description: "We quickly turn ideas into testable prototypes, allowing us to validate concepts and iterate efficiently."
    },
    {
      icon: <Code className="h-8 w-8" />,
      title: "Technology Leadership",
      description: "We stay at the forefront of technological advancement, adopting new tools and methodologies that enhance our capabilities."
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "User-Centered Design",
      description: "All our innovations are driven by real user needs and designed to solve genuine problems in meaningful ways."
    }
  ];

  const innovationMethods = [
    {
      title: "Research & Development",
      description: "We invest significantly in R&D to explore emerging technologies and their potential applications.",
      examples: ["AI and machine learning integration", "Advanced user interface design", "Cloud-native architecture exploration"]
    },
    {
      title: "Continuous Learning",
      description: "Our team regularly engages with the latest industry developments and best practices.",
      examples: ["Technical conferences and workshops", "Online learning platforms", "Industry research and publications"]
    },
    {
      title: "Experimental Projects",
      description: "We allocate time for experimental projects that may lead to breakthrough innovations.",
      examples: ["Hackathons and innovation challenges", "Side projects and proof-of-concepts", "Cross-functional collaboration experiments"]
    },
    {
      title: "Customer Co-Innovation",
      description: "We work closely with customers to understand their challenges and co-create solutions.",
      examples: ["User feedback integration", "Beta testing programs", "Customer advisory panels"]
    }
  ];

  const innovationOutcomes = [
    "GoFella's intuitive productivity platform that adapts to user workflows",
    "Streamlined development processes that reduce time-to-market by 40%",
    "User interface designs that require 60% less training time",
    "Integration capabilities that connect with 95% of popular business tools",
    "Accessibility features that make our products usable by diverse user groups"
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-8">
              <Lightbulb className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Innovation
            </h1>
            <p className="text-xl text-vincotek-blue font-semibold mb-6">
              Creating Smart Solutions for Real-World Challenges
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Innovation at Vincotek isn't just about using the latest technology—it's about finding creative, 
              practical solutions to real problems that make a meaningful difference in people's lives and work. 
              We believe that true innovation combines cutting-edge thinking with deep understanding of human needs.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Innovation is not about having the newest technology, but about solving problems in ways that truly matter."
              </blockquote>
              <p className="text-gray-600">
                — Vincotek Innovation Philosophy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation Principles */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Innovation Principles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide how we approach innovation and ensure our creative efforts deliver real value.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {innovationPrinciples.map((principle, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 group-hover:scale-110 transition-transform duration-300">
                    {principle.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {principle.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Methods */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              How We Innovate
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our systematic approach to innovation ensures that creativity is channeled into practical, impactful solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {innovationMethods.map((method, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-vincotek-dark">
                      {method.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {method.description}
                  </p>
                  <div>
                    <h4 className="text-sm font-semibold text-vincotek-dark mb-3 uppercase tracking-wide">
                      Examples:
                    </h4>
                    <ul className="space-y-2">
                      {method.examples.map((example, exampleIndex) => (
                        <li key={exampleIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm text-gray-600">{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Impact */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                Innovation in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Here are some concrete examples of how our innovation principles have led to real improvements 
                in our products and processes.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-vincotek-dark to-gray-800 rounded-2xl p-8 text-white">
              <div className="flex items-center mb-6">
                <Rocket className="h-8 w-8 text-vincotek-yellow mr-4" />
                <h3 className="text-2xl font-semibold">Innovation Outcomes</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {innovationOutcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-3 h-3 bg-vincotek-yellow rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-300 leading-relaxed">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Innovation */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              The Future of Innovation at Vincotek
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              We're constantly exploring new frontiers in technology and human-computer interaction. 
              Our innovation roadmap includes artificial intelligence integration, advanced automation, 
              and next-generation user experiences that will further simplify and enhance how people work with technology.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products-services">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Explore Our Products
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Learn About Our Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Innovation;